// @ai-generated 2026-08-10T00:00:00+00:00 by kai.cissarek@synqony.com using Claude Opus 5
import { useAuftragRepository } from '@Hooks/useRepository';
import { type ApiContainerAction } from '@Model';
import { ContainerActionsRepository } from '@Repositories/Auftragsbearbeitung';
import { isOk, useToasts } from '@synqo/frontend-component-library';
import { useCallback, useState } from 'react';

export interface MarkContainerForBooking {
    /** Marks one action for booking and reports whether it worked. */
    markForBooking: (actionId: string) => Promise<boolean>;
    /** Marks the given actions and answers with the rows that moved. */
    markGroupForBooking: (actionIds: string[]) => Promise<ApiContainerAction[] | undefined>;
    /** Takes the marking of the given actions back and answers with the rows that moved. */
    unmarkGroupForBooking: (actionIds: string[]) => Promise<ApiContainerAction[] | undefined>;
    /** Whether a mark request is in flight. */
    isMarking: boolean;
}

/**
 * Marks a container action for booking.
 *
 * The booking status is server-owned and never read back from a payload, which is why marking has a
 * route of its own rather than going through a child PUT. The row that comes back carries the new
 * status and is merged into the order in place.
 */
export function useMarkContainerForBooking(
    orderId: string,
    mergeAction: (action: ApiContainerAction) => void,
): MarkContainerForBooking {
    const containerActions = useAuftragRepository(ContainerActionsRepository);
    const { pushAllToasts } = useToasts();
    const [isMarking, setIsMarking] = useState(false);

    const markForBooking = useCallback(
        async (actionId: string) => {
            setIsMarking(true);
            try {
                const result = await containerActions.markForBooking(actionId, orderId);

                pushAllToasts(result.messages);

                if (!isOk(result)) {
                    return false;
                }

                mergeAction(result.data);
                return true;
            } finally {
                setIsMarking(false);
            }
        },
        [containerActions, orderId, mergeAction, pushAllToasts],
    );

    const markGroupForBooking = useCallback(
        async (actionIds: string[]) => {
            setIsMarking(true);
            try {
                const result = await containerActions.markGroupForBooking({ action_ids: actionIds }, orderId);

                pushAllToasts(result.messages);

                return isOk(result) ? result.data : undefined;
            } finally {
                setIsMarking(false);
            }
        },
        [containerActions, orderId, pushAllToasts],
    );

    const unmarkGroupForBooking = useCallback(
        async (actionIds: string[]) => {
            if (actionIds.length === 0) {
                return [];
            }

            setIsMarking(true);
            try {
                const result = await containerActions.unmarkGroupForBooking({ action_ids: actionIds }, orderId);

                pushAllToasts(result.messages);

                return isOk(result) ? result.data : undefined;
            } finally {
                setIsMarking(false);
            }
        },
        [containerActions, orderId, pushAllToasts],
    );

    return { markForBooking, markGroupForBooking, unmarkGroupForBooking, isMarking };
}
